import * as React from "react";
import { themePreview } from "@/utils/themeUtils";

/**
 * Options for the useThemePreview hook
 */
interface UseThemePreviewOptions {
  /** Delay in ms before showing theme preview when hovering (default: 150ms) */
  delay?: number;
  /** Whether to disable the preview functionality (default: false) */
  disabled?: boolean;
}

/**
 * Custom hook for managing theme preview behavior with hover interactions.
 * Provides mouse event handlers and cleanup functions for theme previewing.
 *
 * @param previewCallback - Function to call when previewing a theme (pass null to clear)
 * @param options - Optional configuration for preview behavior
 * @returns Object containing event handlers and cleanup functions
 *
 * @example
 * ```tsx
 * const { handleMouseEnter, handleMouseLeave, handleClose, cleanup } = useThemePreview(
 *   previewTheme,
 *   { delay: 200, disabled: false }
 * );
 *
 * // Use in component
 * <MenuItem
 *   onMouseEnter={() => handleMouseEnter(themeId)}
 *   onMouseLeave={handleMouseLeave}
 * />
 * ```
 */
export const useThemePreview = <T extends string = string>(
  previewCallback: (value: T | null) => void,
  options?: UseThemePreviewOptions,
) => {
  const { delay = 150, disabled = false } = options || {};

  // Create preview handlers using themePreview utilities
  const { handleMouseEnter, handleMouseLeave, cleanup } = React.useMemo(
    () =>
      themePreview.createPreviewHandlers({
        disablePreview: disabled,
        previewCallback: previewCallback as (value: string | null) => void,
        previewDelay: delay,
      }),
    [previewCallback, delay, disabled],
  );

  // Clean up preview timer on unmount
  React.useEffect(() => () => cleanup(), [cleanup]);

  // Handle close/cancel - clear preview
  const handleClose = React.useCallback(() => {
    previewCallback(null);
  }, [previewCallback]);

  return {
    /** Manual cleanup function (usually not needed due to auto-cleanup) */
    cleanup,
    /** Handler to clear preview (e.g., when dropdown closes) */
    handleClose,
    /** Handler for mouse enter events - pass the theme/scheme ID to preview */
    handleMouseEnter: handleMouseEnter as (value: T) => void,
    /** Handler for mouse leave events */
    handleMouseLeave,
  };
};
